import { useEffect, useState } from "react";

/**
 * Unified responsive device detection hook
 * Combines device type detection (mobile/tablet/desktop) with breakpoints and orientation
 * 
 * Key difference from useBreakpoint:
 * - useBreakpoint: Screen size only (width-based)
 * - useResponsiveDevice: Actual device type + screen size + orientation
 * 
 * Use this hook when you need to determine UI behavior (dialogs, panels, layouts)
 */

export interface ResponsiveDeviceInfo {
  // Actual device type (based on User Agent + touch capability)
  isMobileDevice: boolean;
  isTabletDevice: boolean;
  isDesktopDevice: boolean;
  deviceType: "mobile" | "tablet" | "desktop";
  
  // Screen size breakpoints (width-based)
  isMobileWidth: boolean;
  isTabletWidth: boolean;
  isDesktopWidth: boolean;
  
  // Orientation
  isLandscape: boolean;
  isPortrait: boolean;
  
  // Combined flags for UI decisions
  shouldUseMobileUI: boolean; // True for mobile devices regardless of orientation
  shouldUseTabletUI: boolean; // True for tablets
  shouldUseDesktopUI: boolean; // True for desktop
  
  // Dimensions
  width: number;
  height: number;
  
  // OS info
  os: "ios" | "android" | "windows" | "macos" | "linux" | "unknown";
  isTouchDevice: boolean;
}

/**
 * The screen's own orientation, which the on-screen keyboard never changes.
 * Null when the browser reports none.
 */
function screenIsLandscape(): boolean | null {
  const type = window.screen?.orientation?.type;
  if (type) return type.startsWith("landscape");
  // iOS Safari before 16.4 has no `screen.orientation`, only the older angle.
  const angle = (window as { orientation?: unknown }).orientation;
  if (typeof angle === "number") return Math.abs(angle) === 90;
  return null;
}

export function getDeviceInfo(): ResponsiveDeviceInfo {
  const userAgent = navigator.userAgent;
  const width = window.innerWidth;
  const height = window.innerHeight;
  
  // Detect device type by User Agent
  const isMobileUA = /Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isTabletUA = /iPad|Android(?!.*Mobile)/i.test(userAgent);
  const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  
  // Screen size breakpoints
  const isMobileWidth = width < 768;
  const isTabletWidth = width >= 768 && width < 1024;
  const isDesktopWidth = width >= 1024;
  
  // Orientation
  //
  // A phone or tablet is in landscape when its screen is, not when the viewport
  // is wider than tall. index.html lets the on-screen keyboard resize the
  // viewport (`interactive-widget=resizes-content`), so on a portrait phone the
  // keyboard alone made `width > height`, and every `isMobileDevice && isLandscape`
  // layout flipped while typing: Create Poll left its bottom sheet for the
  // right-hand side panel, a gap open on its left. A desktop window has no
  // keyboard to shrink it, so its shape still decides.
  const isLandscape =
    isMobileUA || (isTabletUA && isTouchDevice)
      ? screenIsLandscape() ?? width > height
      : width > height;
  const isPortrait = !isLandscape;
  
  // Determine actual device type
  // Priority: User Agent > Touch + Width
  let isMobileDevice: boolean;
  let isTabletDevice: boolean;
  let isDesktopDevice: boolean;
  
  if (isMobileUA) {
    // Definitely a mobile device (phone)
    isMobileDevice = true;
    isTabletDevice = false;
    isDesktopDevice = false;
  } else if (isTabletUA && isTouchDevice) {
    // Definitely a tablet (need both UA AND touch)
    isMobileDevice = false;
    isTabletDevice = true;
    isDesktopDevice = false;
  } else {
    // Desktop - includes touch-enabled laptops and resized browser windows
    // DO NOT use touch+width combo as it misidentifies touch laptops
    isMobileDevice = false;
    isTabletDevice = false;
    isDesktopDevice = true;
  }
  
  const deviceType: "mobile" | "tablet" | "desktop" = 
    isMobileDevice ? "mobile" : isTabletDevice ? "tablet" : "desktop";
  
  // UI decision flags
  // Mobile devices should ALWAYS use mobile UI (portrait or landscape)
  // Tablets should use tablet UI (can be different from mobile)
  // Desktop should use desktop UI
  const shouldUseMobileUI = isMobileDevice;
  const shouldUseTabletUI = isTabletDevice;
  const shouldUseDesktopUI = isDesktopDevice;
  
  // Detect OS
  let os: ResponsiveDeviceInfo["os"] = "unknown";
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    os = "ios";
  } else if (/Android/i.test(userAgent)) {
    os = "android";
  } else if (/Win/i.test(userAgent)) {
    os = "windows";
  } else if (/Mac/i.test(userAgent)) {
    os = "macos";
  } else if (/Linux/i.test(userAgent)) {
    os = "linux";
  }
  
  return {
    isMobileDevice,
    isTabletDevice,
    isDesktopDevice,
    deviceType,
    isMobileWidth,
    isTabletWidth,
    isDesktopWidth,
    isLandscape,
    isPortrait,
    shouldUseMobileUI,
    shouldUseTabletUI,
    shouldUseDesktopUI,
    width,
    height,
    os,
    isTouchDevice,
  };
}

export function useResponsiveDevice(): ResponsiveDeviceInfo {
  const [deviceInfo, setDeviceInfo] = useState<ResponsiveDeviceInfo>(() => 
    getDeviceInfo()
  );
  
  useEffect(() => {
    const handleResize = () => {
      setDeviceInfo(getDeviceInfo());
    };
    
    // A rotation's `resize` can arrive before the screen reports its new
    // orientation; the screen's own `change` settles it.
    const screenOrientation = window.screen?.orientation;
    
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    screenOrientation?.addEventListener?.("change", handleResize);
    
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      screenOrientation?.removeEventListener?.("change", handleResize);
    };
  }, []);
  
  return deviceInfo;
}

/**
 * Standalone function to check if device should use mobile UI
 * Can be used outside React components
 */
export function shouldUseMobileUI(): boolean {
  const userAgent = navigator.userAgent;
  const isMobileUA = /Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  return isMobileUA;
}
